import { Navigate, useLocation } from "react-router-dom";
import { useCompanyCapabilities } from "../../hooks/useCompanyCapabilities";
import { CardComp } from "..";

function Unavailable({ title, message }) {
  return (
    <CardComp cardClassName="border-0 shadow-sm rounded-4 my-4">
      <div className="text-center py-5 px-3">
        <h5 className="fw-bold mb-2">{title}</h5>
        <p className="text-muted mb-0">{message}</p>
      </div>
    </CardComp>
  );
}

/**
 * Maps member/investor /invest/* paths to required capability keys.
 */
export function getRequiredMemberInvestorCapabilityForPath(pathname) {
  const path = (pathname || "").replace(/\/$/, "");

  if (path.startsWith("/invest/investments")) {
    return "investment";
  }
  if (path.startsWith("/invest/marketplace")) {
    return "marketplace";
  }
  if (path.startsWith("/invest/real-estate") || path.includes("filter=real_estate")) {
    return "realEstate";
  }
  if (path.startsWith("/invest/cooperative-request")) {
    return "cooperativeRequest";
  }
  if (path.startsWith("/invest/kyc") || path.startsWith("/invest/profile")) {
    return "investorPortal";
  }

  return null;
}

export default function InvestPathCapabilityGuard({ children }) {
  const { pathname, search } = useLocation();
  const caps = useCompanyCapabilities();
  const fullPath = `${pathname}${search || ""}`;
  const required = getRequiredMemberInvestorCapabilityForPath(fullPath);

  if (!required) {
    return children;
  }

  if (required === "investment" && !caps.investment) {
    return (
      <Unavailable
        title="Investments unavailable"
        message="Investment opportunities are not enabled for this cooperative."
      />
    );
  }

  if (required === "marketplace" && !caps.marketplace) {
    return (
      <Unavailable
        title="Marketplace unavailable"
        message="Marketplace is not enabled for this cooperative."
      />
    );
  }

  if (required === "realEstate" && !caps.realEstate) {
    return (
      <Unavailable
        title="Real estate requests unavailable"
        message="Real estate requests are not enabled for this cooperative."
      />
    );
  }

  if (required === "cooperativeRequest" && !caps.cooperativeRequest) {
    return <Navigate to="/invest/requests" replace />;
  }

  if (required === "investorPortal" && !caps.investorPortal) {
    const hasOther =
      caps.investment || caps.marketplace || caps.realEstate || caps.cooperativeRequest;
    if (hasOther) {
      return (
        <Unavailable
          title="Profile unavailable"
          message="Investor account management is not enabled for this cooperative."
        />
      );
    }
  }

  return children;
}
